import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { siteConfig } from "../../lib/siteConfig";
import {
  BarChart3,
  Bot,
  ClipboardList,
  CreditCard,
  FileCheck2,
  Gamepad2,
  Gift,
  LayoutGrid,
  LogOut,
  Megaphone,
  Menu,
  MessageCircle,
  ReceiptText,
  Send,
  Settings,
  Share2,
  Star,
  Trophy,
  UserPlus,
  Users,
  WalletCards,
  X,
} from "lucide-react";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", shortLabel: "Dashboard", icon: LayoutGrid, permission: "dashboard" },
  { to: "/admin/members", label: "Users", shortLabel: "Users", icon: Users, adminOnly: true },
  { to: "/admin/users", label: "Members", shortLabel: "Members", icon: Users, permission: "members" },
  { to: "/admin/games", label: "Games", shortLabel: "Games", icon: Gamepad2, permission: "games" },
  { to: "/admin/automation", label: "Backend Attachments", shortLabel: "Backend", icon: Bot },
  { to: "/admin/game-credits", label: "Game Credits", shortLabel: "Game Credits", icon: WalletCards, permission: "game_credits" },
  { to: "/admin/requests", label: "Player Requests", shortLabel: "Requests", icon: ClipboardList, permission: "player_requests" },
  { to: "/admin/payment-methods", label: "Payment Methods", shortLabel: "Methods", icon: CreditCard, permission: "payment_methods" },
  { to: "/admin/payment-flows", label: "Payment Flows", shortLabel: "Flows", icon: ReceiptText, adminOnly: true },
  { to: "/admin/wallet", label: "Wallet", shortLabel: "Wallet", icon: WalletCards, permission: "wallet" },
  { to: "/admin/free-credits", label: "Free Credits", shortLabel: "Free Credits", icon: Gift, permission: "free_credits" },
  { to: "/admin/transactions", label: "Transactions", shortLabel: "Transactions", icon: ReceiptText, permission: "transactions" },
  { to: "/admin/payments-review", label: "Payments Review", shortLabel: "Payments", icon: FileCheck2, permission: "payments_review" },
  { to: "/admin/payouts-review", label: "Payouts Review", shortLabel: "Payouts", icon: WalletCards, permission: "payouts_review" },
  { to: "/admin/analytics", label: "Analytics", shortLabel: "Analytics", icon: BarChart3 },
  { to: "/admin/reviews", label: "Player Reviews", shortLabel: "Reviews", icon: Star, adminOnly: true },
  { to: "/admin/depositors", label: "Depositors", shortLabel: "Depositors", icon: UserPlus, permission: "depositors" },
  { to: "/admin/telegram", label: "Telegram", shortLabel: "Telegram", icon: Send },
  { to: "/admin/communications", label: "Communications", shortLabel: "Notice", icon: Megaphone, permission: "communications" },
  { to: "/admin/support-app", label: "Support App", shortLabel: "Support", icon: MessageCircle, permission: "support_app" },
  { to: "/admin/client-referrals", label: "Client Referrals", shortLabel: "Referrals", icon: Share2, permission: "client_referrals" },
  { to: "/admin/rewards", label: "Rewards", shortLabel: "Rewards", icon: Trophy },
  { to: "/admin/plinko", label: "Plinko", shortLabel: "Plinko", icon: Gamepad2 },
  { to: "/admin/crash", label: "Crash", shortLabel: "Crash", icon: Gamepad2 },
  { to: "/admin/settings", label: "Settings", shortLabel: "Settings", icon: Settings },
];

const ADMIN_SESSION_CHECK_MS = 60000;
const ADMIN_SUPPORT_STATS_MS = 3000;
const ADMIN_SESSION_MIN_MS = 30000;
const ADMIN_SUPPORT_STATS_MIN_MS = 2500;

const shouldForceMobileAdminNav = () => {
  if (typeof window === "undefined") return false;
  const nativeApp = Boolean(window.Capacitor?.isNativePlatform?.());
  const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches;
  const touchPoints = navigator.maxTouchPoints || 0;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  const screenWidth = window.screen?.width || viewportWidth;
  const screenHeight = window.screen?.height || window.innerHeight || screenWidth;
  const smallestScreenSide = Math.min(screenWidth, screenHeight);
  return Boolean(nativeApp || ((coarsePointer || touchPoints > 0) && (viewportWidth <= 1024 || smallestScreenSide <= 900)));
};

export default function AdminLayout() {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [forceMobileNav, setForceMobileNav] = useState(shouldForceMobileAdminNav);
  const sessionCheckRef = useRef({ last: 0, running: false });
  const supportStatsRef = useRef({ last: 0, running: false });
  const supportStandaloneMode = location.pathname === "/admin/support-app";
  const supportAppMode = supportStandaloneMode || location.pathname === "/admin/chat" || location.pathname === "/admin/support";

  useEffect(() => {
    document.body.classList.add("admin-layout-open");
    return () => document.body.classList.remove("admin-layout-open");
  }, []);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const updateMobileNavMode = () => {
      setForceMobileNav(shouldForceMobileAdminNav());
    };

    updateMobileNavMode();
    window.addEventListener("resize", updateMobileNavMode);
    window.addEventListener("orientationchange", updateMobileNavMode);
    return () => {
      window.removeEventListener("resize", updateMobileNavMode);
      window.removeEventListener("orientationchange", updateMobileNavMode);
    };
  }, []);

  useEffect(() => {
    if (loading || !user || !["admin", "staff"].includes(user.role)) return undefined;

    let cancelled = false;
    const forceLogout = () => {
      if (cancelled) return;
      logout();
      nav("/admin", { replace: true });
    };
    const checkSession = async ({ force = false } = {}) => {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      if (sessionCheckRef.current.running || (!force && now - sessionCheckRef.current.last < ADMIN_SESSION_MIN_MS)) return;
      sessionCheckRef.current.last = now;
      sessionCheckRef.current.running = true;
      try {
        await api.get("/auth/me", { skipMaintenance: true, timeout: 7000 });
      } catch (error) {
        if (error?.response?.status === 401) forceLogout();
      } finally {
        sessionCheckRef.current.running = false;
      }
    };
    const handleVisible = () => {
      if (document.visibilityState === "visible") checkSession();
    };

    const timer = window.setInterval(checkSession, ADMIN_SESSION_CHECK_MS);
    window.addEventListener("focus", checkSession);
    window.addEventListener("pageshow", checkSession);
    window.addEventListener("online", checkSession);
    document.addEventListener("visibilitychange", handleVisible);
    checkSession({ force: true });

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", checkSession);
      window.removeEventListener("pageshow", checkSession);
      window.removeEventListener("online", checkSession);
      document.removeEventListener("visibilitychange", handleVisible);
    };
  }, [loading, logout, nav, user]);

  const loadSupportStats = useCallback(async () => {
    if (loading || !user || !["admin", "staff"].includes(user.role)) return;
    if (document.visibilityState === "hidden") return;
    const now = Date.now();
    if (supportStatsRef.current.running || now - supportStatsRef.current.last < ADMIN_SUPPORT_STATS_MIN_MS) return;
    supportStatsRef.current.last = now;
    supportStatsRef.current.running = true;
    const canUseSupport = user.role === "admin" || (user.admin_permissions || []).includes("support_app");
    if (!canUseSupport) {
      setSupportUnreadCount(0);
      supportStatsRef.current.running = false;
      return;
    }
    try {
      const { data } = await api.get("/admin/support/stats", { skipMaintenance: true, timeout: 7000 });
      setSupportUnreadCount(Number(data?.unread_count || 0));
    } catch {
      setSupportUnreadCount(0);
    } finally {
      supportStatsRef.current.running = false;
    }
  }, [loading, user]);

  useEffect(() => {
    loadSupportStats();
    const timer = window.setInterval(loadSupportStats, ADMIN_SUPPORT_STATS_MS);
    window.addEventListener("focus", loadSupportStats);
    window.addEventListener("pageshow", loadSupportStats);
    window.addEventListener("online", loadSupportStats);
    document.addEventListener("visibilitychange", loadSupportStats);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", loadSupportStats);
      window.removeEventListener("pageshow", loadSupportStats);
      window.removeEventListener("online", loadSupportStats);
      document.removeEventListener("visibilitychange", loadSupportStats);
    };
  }, [loadSupportStats]);

  if (loading) {
    return (
      <div className="grid min-h-[100dvh] place-items-center bg-[#0A0A0B] px-6 text-center text-white">
        <div>
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-white/15 border-t-[#2F6FA5]" />
          <div className="mt-4 text-sm font-semibold">Loading admin app...</div>
          <div className="mt-1 text-xs text-slate-500">Checking your secure session</div>
        </div>
      </div>
    );
  }
  if (!user || !["admin", "staff"].includes(user.role)) return <Navigate to="/admin" replace />;

  const isOwner = user.role === "admin";
  const permissions = new Set(user.admin_permissions || []);
  const visibleNav = NAV.filter((item) => isOwner || (!item.adminOnly && item.permission && permissions.has(item.permission)));
  const canUseCurrentRoute = isOwner || visibleNav.some((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));
  if (!canUseCurrentRoute) return <Navigate to={visibleNav[0]?.to || "/admin"} replace />;
  const currentNav = visibleNav.find((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));

  return (
    <div className={`admin-layout-root safe-area-admin-layout ${supportAppMode ? "admin-layout-support-mode" : ""} ${forceMobileNav ? "admin-force-mobile-nav" : ""} min-h-screen bg-[#0A0A0B] text-slate-200 flex flex-col md:flex-row`}>
      {!supportStandaloneMode && (
        <header className="admin-mobile-header flex h-14 shrink-0 items-center justify-between border-b border-white/10 bg-[#0A0A0B] px-3 md:hidden">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-white"
            aria-label="Open admin menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0 px-3 text-center">
            <div className="truncate text-sm font-bold text-white">{currentNav?.shortLabel || currentNav?.label || "Admin"}</div>
            <div className="truncate text-[11px] text-slate-500">{siteConfig.name}</div>
          </div>
          <button
            type="button"
            onClick={() => {
              logout();
              nav("/admin");
            }}
            className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-slate-400"
            aria-label="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </header>
      )}

      <aside className={`admin-layout-sidebar ${supportAppMode ? "hidden lg:flex" : "hidden md:flex"} w-32 shrink-0 border-r border-white/10 bg-[#0A0A0B] flex-col sm:w-40 lg:w-60`}>
        <div className="px-3 py-4 border-b border-white/10 flex items-center justify-start gap-2 lg:px-5 lg:py-5">
          <img src={siteConfig.logoUrl} alt={siteConfig.name} className="w-8 h-8 rounded-full object-cover ring-1 ring-[#2F6FA5]/50" />
          <div className="min-w-0">
            <div className="text-sm font-semibold leading-tight">{siteConfig.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Operator</div>
          </div>
        </div>
        <nav className="flex-1 p-2 overflow-y-auto">
          {visibleNav.map(({ to, label, shortLabel, icon: Icon }) => {
            const badge = to === "/admin/support-app" ? supportUnreadCount : 0;
            return (
            <NavLink key={to} to={to} data-testid={`admin-nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex min-w-0 items-center justify-start gap-2 rounded-md px-2 py-2 text-xs mb-1 transition-colors sm:text-sm lg:gap-3 lg:px-3 ${
                  isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"
                }`
              }>
              <Icon className="w-4 h-4 shrink-0" />
              <span className="min-w-0 truncate lg:hidden">{shortLabel || label}</span>
              <span className="hidden min-w-0 truncate lg:inline">{label}</span>
              {badge > 0 && (
                <span className="ml-auto grid min-w-[1.25rem] shrink-0 place-items-center rounded-full bg-[#2F6FA5] px-1.5 py-0.5 text-[10px] font-black leading-none text-white">
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-2 py-2 mb-2 text-[11px] lg:px-3 lg:text-xs">
            <div className="text-slate-300 truncate">{user.email}</div>
            <div className="text-slate-500">{isOwner ? "Operator" : "Staff"}</div>
          </div>
          <button onClick={() => { logout(); nav("/admin"); }} data-testid="admin-logout"
            className="w-full flex min-w-0 items-center justify-start gap-2 px-2 py-2 rounded-md text-xs text-slate-400 hover:bg-white/5 hover:text-white sm:text-sm lg:px-3">
            <LogOut className="w-4 h-4 shrink-0" />
            <span className="min-w-0 truncate">Sign out</span>
          </button>
        </div>
      </aside>

      {!supportStandaloneMode && mobileNavOpen && (
        <div className="admin-mobile-nav fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/65"
            onClick={() => setMobileNavOpen(false)}
            aria-label="Close admin menu"
          />
          <aside className="admin-mobile-drawer absolute left-0 top-0 flex h-full w-[min(82vw,20rem)] flex-col border-r border-white/10 bg-[#0A0A0B] shadow-2xl shadow-black/60">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-4">
              <div className="flex min-w-0 items-center gap-3">
                <img src={siteConfig.logoUrl} alt={siteConfig.name} className="h-9 w-9 rounded-full object-cover ring-1 ring-[#2F6FA5]/50" />
                <div className="min-w-0">
                  <div className="truncate text-sm font-bold text-white">{siteConfig.name}</div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">Operator</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-slate-300"
                aria-label="Close admin menu"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <nav className="min-h-0 flex-1 overflow-y-auto p-3">
              {visibleNav.map(({ to, label, shortLabel, icon: Icon }) => {
                const badge = to === "/admin/support-app" ? supportUnreadCount : 0;
                const active = location.pathname === to || location.pathname.startsWith(`${to}/`);
                return (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={`admin-mobile-nav-${label.toLowerCase()}`}
                    className={`mb-1 flex min-w-0 items-center justify-start gap-3 rounded-md px-3 py-3 text-sm transition-colors ${
                      active ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="min-w-0 flex-1 truncate">{shortLabel || label}</span>
                    {badge > 0 && (
                      <span className="grid min-w-[1.35rem] shrink-0 place-items-center rounded-full bg-[#2F6FA5] px-1.5 py-0.5 text-[10px] font-black leading-none text-white">
                        {badge > 99 ? "99+" : badge}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </nav>
            <div className="border-t border-white/10 p-4">
              <div className="mb-3 min-w-0 text-xs">
                <div className="truncate text-slate-300">{user.email}</div>
                <div className="text-slate-500">{isOwner ? "Operator" : "Staff"}</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  logout();
                  nav("/admin");
                }}
                className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-sm text-slate-400 hover:bg-white/5 hover:text-white"
              >
                <LogOut className="h-4 w-4 shrink-0" />
                Sign out
              </button>
            </div>
          </aside>
        </div>
      )}

      <main className={`admin-layout-main flex-1 min-w-0 ${supportAppMode ? "overflow-hidden" : "overflow-auto"}`}>
        <Outlet />
      </main>
    </div>
  );
}
